import api from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userAgent?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converts a URL-safe base64 string (as returned by the VAPID public-key
 * endpoint) to the Uint8Array format required by PushManager.subscribe.
 *
 * Standard Web Push utility — see RFC 8291.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  // Restore padding and convert URL-safe chars
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Returns true when the current environment supports Web Push.
 *
 * iOS Safari note: Push is only available when the PWA is running in
 * standalone mode (added to home screen) on iOS 16.4+. We check for
 * standalone display mode before reporting support — never throw on
 * unsupported browsers.
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;

  // iOS Safari: Push is only available in installed (standalone) PWAs.
  // navigator.standalone is an Apple-specific boolean set to true when
  // launched from the home screen. matchMedia covers the spec-compliant path.
  const isIOS =
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS) {
    const standalone =
      ('standalone' in navigator && (navigator as { standalone: boolean }).standalone === true) ||
      window.matchMedia('(display-mode: standalone)').matches;
    if (!standalone) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// VAPID key fetch
// ---------------------------------------------------------------------------

export async function getVapidPublicKey(): Promise<string> {
  const response = await api.get<{ publicKey: string }>('/push/vapid-public-key');
  return response.data.publicKey;
}

// ---------------------------------------------------------------------------
// Subscribe
// ---------------------------------------------------------------------------

/**
 * Full subscribe flow:
 * 1. Check browser support and permission
 * 2. Fetch VAPID public key
 * 3. Subscribe via PushManager
 * 4. POST the subscription to the backend
 *
 * Returns the PushSubscription on success, or null if the browser does not
 * support Web Push or the user denies permission.
 *
 * Never throws — errors are returned as null so callers can handle them
 * gracefully (e.g. show a toast, hide the prompt).
 */
export async function subscribePush(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const registration = await navigator.serviceWorker.ready;

  const vapidKey = await getVapidPublicKey();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  });

  const subscriptionJson = subscription.toJSON();
  const keys = subscriptionJson.keys as { p256dh: string; auth: string } | undefined;

  if (!keys?.p256dh || !keys?.auth) {
    throw new Error('PushSubscription keys missing');
  }

  const payload: PushSubscriptionPayload = {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: keys.p256dh,
      auth: keys.auth,
    },
    userAgent: navigator.userAgent,
  };

  await api.post('/push/subscribe', payload);

  return subscription;
}

// ---------------------------------------------------------------------------
// Unsubscribe
// ---------------------------------------------------------------------------

/**
 * Unsubscribes from PushManager and removes the subscription from the backend.
 * Safe to call even if no active subscription exists.
 */
export async function unsubscribePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await api.delete('/push/subscribe', { data: { endpoint } });
}
